import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  getDailyPlan,
  getLatestSensorReadings,
  getOvenConfig,
  getOvenAutomationRules,
  startBatch,
  startTimer,
  finishBatch,
  listBlends,
  Blend,
  RoastBatch,
  OvenQueues,
  SensorReading,
  OvenTriggerRule,
} from '../api/client';
import { useAuth } from './AuthContext';
import { localDateString } from '../utils/date';
import { ovenQueueHead } from '../utils/ovenQueue';
import { OvenTriggerTracker } from '../utils/ovenTriggers';

const OVEN_IDS = ['1', '2'];

interface AppDataContextType {
  todayBatches: RoastBatch[];
  strayOccupants: RoastBatch[];
  ovenQueues: OvenQueues;
  planError: string | null;
  blends: Blend[];
  allSensorReadings: SensorReading[];
  isLoading: boolean;
  refreshPlan: () => Promise<void>;
  refreshBlends: () => Promise<Blend[] | undefined>;
  updateBlends: (blends: Blend[]) => void;
}

const AppDataContext = createContext<AppDataContextType>({
  todayBatches: [],
  strayOccupants: [],
  ovenQueues: {},
  planError: null,
  blends: [],
  allSensorReadings: [],
  isLoading: true,
  refreshPlan: async () => {},
  refreshBlends: async () => undefined,
  updateBlends: () => {},
});

export function useAppData(): AppDataContextType {
  return useContext(AppDataContext);
}

export function AppDataProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [todayBatches, setTodayBatches] = useState<RoastBatch[]>([]);
  const [strayOccupants, setStrayOccupants] = useState<RoastBatch[]>([]);
  const [ovenQueues, setOvenQueues] = useState<OvenQueues>({});
  const [planError, setPlanError] = useState<string | null>(null);
  const [blends, setBlends] = useState<Blend[]>([]);
  const blendsRequestRef = useRef(0);
  const updateBlends = useCallback((data: Blend[]) => {
    blendsRequestRef.current += 1;
    setBlends(data);
  }, []);
  const [allSensorReadings, setAllSensorReadings] = useState<SensorReading[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Trigger rules per oven — loaded once, refreshed every 60s.
  const rulesRef = useRef<Record<string, OvenTriggerRule[]>>({});
  // Automation must only inspect the Modbus device assigned to each oven.
  const modbusDevicesRef = useRef<Record<string, string>>({});
  const triggerTrackerRef = useRef(new OvenTriggerTracker());
  // Prevent double-firing while an action is in flight
  const firingRef = useRef<Set<string>>(new Set());
  // Latest batches ref — needed inside fetchSensors without stale closure
  const planRef = useRef<{ batches: RoastBatch[]; queues: OvenQueues }>({ batches: [], queues: {} });
  const planRequestRef = useRef(0);
  const sensorsInFlightRef = useRef(false);

  const fetchPlan = useCallback(async () => {
    if (document.hidden) return;
    const request = ++planRequestRef.current;
    try {
      // Recalculate on every poll so the factory day rolls over in local time.
      const data = await getDailyPlan(localDateString(), true);
      if (request !== planRequestRef.current) return;
      planRef.current = { batches: [...data.batches, ...data.stray_occupants], queues: data.oven_queues };
      setTodayBatches(prev =>
        JSON.stringify(prev) === JSON.stringify(data.batches) ? prev : data.batches,
      );
      setStrayOccupants(prev =>
        JSON.stringify(prev) === JSON.stringify(data.stray_occupants) ? prev : data.stray_occupants,
      );
      setOvenQueues(data.oven_queues);
      setPlanError(null);
      setIsLoading(false);
    } catch (error) {
      if (request !== planRequestRef.current) return;
      console.error('Failed to refresh plan:', error);
      setPlanError('לא ניתן לרענן את התכנית ותורי התנורים. בדקו את החיבור ואת מצב המערכת.');
      setIsLoading(false);
    }
  }, []);

  const fetchBlends = useCallback(async () => {
    if (document.hidden) return;
    const request = ++blendsRequestRef.current;
    try {
      const data = await listBlends();
      if (request !== blendsRequestRef.current) return;
      setBlends(prev => JSON.stringify(prev) === JSON.stringify(data) ? prev : data);
      return data;
    } catch (error) {
      console.error('Failed to refresh blends:', error);
    }
  }, []);

  const fetchRules = useCallback(async () => {
    await Promise.all(OVEN_IDS.map(async (id) => {
      try {
        const [rulesConfig, ovenConfig] = await Promise.all([
          getOvenAutomationRules(id),
          getOvenConfig(id),
        ]);
        const rules = rulesConfig.rules.filter(r => r.enabled);
        const modbusDeviceId = ovenConfig.modbus_device_id || undefined;
        if (JSON.stringify(rulesRef.current[id]) !== JSON.stringify(rules)
          || modbusDevicesRef.current[id] !== modbusDeviceId) {
          triggerTrackerRef.current.reset();
        }
        rulesRef.current[id] = rules;
        if (modbusDeviceId) {
          modbusDevicesRef.current[id] = modbusDeviceId;
        } else {
          delete modbusDevicesRef.current[id];
        }
      } catch (error) {
        console.error(`Failed to refresh automation rules for oven ${id}:`, error);
        triggerTrackerRef.current.reset();
        rulesRef.current[id] = [];
        delete modbusDevicesRef.current[id];
      }
    }));
  }, []);

  const evaluateTriggers = useCallback((readings: SensorReading[]) => {
    const { batches, queues } = planRef.current;
    for (const ovenId of OVEN_IDS) {
      const rules = rulesRef.current[ovenId] ?? [];
      const modbusDeviceId = modbusDevicesRef.current[ovenId];
      if (!modbusDeviceId) continue;
      const ovenReadings = readings.filter(r => r.device_id === modbusDeviceId);
      const activeBatch = batches.find(b => b.machine_id === ovenId && b.status === 'in-progress') ?? null;
      const plannedBatch = !activeBatch ? ovenQueueHead(queues, ovenId, batches) : null;

      for (const [ruleIndex, rule] of rules.entries()) {
        const key = `${ovenId}:${rule.event_type}`;
        if (firingRef.current.has(key)) continue;

        const eligibleBatch = rule.event_type === 'roast_start'
          ? plannedBatch
          : activeBatch?.timer_started_at ? activeBatch : null;
        const reading = ovenReadings.find(r => r.register_name === rule.register_name);
        if (triggerTrackerRef.current.evaluate(
          `${ovenId}:${ruleIndex}`,
          rule,
          reading,
          eligibleBatch?.id ?? null,
        )) {
          if (rule.event_type === 'roast_start' && plannedBatch) {
            firingRef.current.add(key);
            console.log(`[trigger] roast_start oven=${ovenId} batch=${plannedBatch.id}`);
            startBatch(plannedBatch.id, ovenId)
              .then(batch => startTimer(batch.id, 'sensor'))
              .then(() => fetchPlan())
              .catch(e => console.error('[trigger] startBatch/startTimer failed', e))
              .finally(() => firingRef.current.delete(key));
          }

          if (rule.event_type === 'roast_end' && activeBatch) {
            firingRef.current.add(key);
            console.log(`[trigger] roast_end oven=${ovenId} batch=${activeBatch.id}`);
            finishBatch(activeBatch.id)
              .then(() => fetchPlan())
              .catch(e => console.error('[trigger] finishBatch failed', e))
              .finally(() => firingRef.current.delete(key));
          }
        }
      }
    }
  }, [fetchPlan]);

  const fetchSensors = useCallback(async () => {
    if (document.hidden || sensorsInFlightRef.current) return;
    sensorsInFlightRef.current = true;
    try {
      const data = await getLatestSensorReadings();
      setAllSensorReadings(prev =>
        JSON.stringify(prev) === JSON.stringify(data) ? prev : data,
      );
      evaluateTriggers(data);
    } catch (error) {
      console.error('Failed to refresh sensor readings:', error);
      triggerTrackerRef.current.reset();
    } finally {
      sensorsInFlightRef.current = false;
    }
  }, [evaluateTriggers]);

  // Only poll when the user is authenticated to avoid 401 loops on the login page.
  useEffect(() => {
    if (!user) return;

    fetchPlan();
    fetchBlends();
    fetchSensors();
    fetchRules();

    const planId = setInterval(fetchPlan, 5000);
    const blendsId = setInterval(fetchBlends, 30000);
    const sensorsId = setInterval(fetchSensors, 5000);
    // Refresh rules every 60s so config changes take effect without page reload
    const rulesId = setInterval(fetchRules, 60000);

    return () => {
      clearInterval(planId);
      clearInterval(blendsId);
      clearInterval(sensorsId);
      clearInterval(rulesId);
      triggerTrackerRef.current.reset();
    };
  }, [user, fetchPlan, fetchBlends, fetchSensors, fetchRules]);

  return (
    <AppDataContext.Provider
      value={{
        todayBatches,
        strayOccupants,
        ovenQueues,
        planError,
        blends,
        allSensorReadings,
        isLoading,
        refreshPlan: fetchPlan,
        refreshBlends: fetchBlends,
        updateBlends,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
}
